import React from "react";
import type { WorkflowData } from "@/types";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { ArrowRight, Wrench, Target } from "lucide-react";

interface Props {
  workflowData: WorkflowData | null;
  analysis: string;
  summary: string;
}

const SandboxCanvas: React.FC<Props> = ({ workflowData, analysis, summary }) => {
  if (!workflowData) {
    return (
      <div className="h-full flex items-center justify-center border-2 border-dashed border-border rounded-xl m-8">
        <div className="text-center text-muted-foreground">
          <p className="text-xl font-semibold mb-2">No Workflow Yet</p>
          <p>Describe your idea in the chat to generate a visual plan.</p>
        </div>
      </div>
    );
  }

  const { title, phase, cause, tools, steps, diagram, tags } = workflowData;

  return (
    <div className="h-full overflow-auto p-8">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header Section */}
        <div>
          <h2 className="text-3xl font-bold font-display mb-2">{title}</h2>
          <div className="flex items-center gap-2 mb-4">
            <Badge variant="secondary">{phase}</Badge>
            {tags?.map((tag, idx) => (
              <Badge key={idx} variant="outline">{tag}</Badge>
            ))}
          </div>
        </div>

        {/* Summary Section */}
        {summary && (
          <Card className="p-4 bg-primary/5 border-primary/20">
            <p className="text-sm">{summary}</p>
          </Card>
        )}

        {/* Analysis Section */}
        {analysis && (
          <Card className="p-4 bg-muted/50">
            <h3 className="font-semibold mb-2 flex items-center gap-2">
              <Target className="h-4 w-4" />
              Analysis
            </h3>
            <p className="text-sm text-muted-foreground">{analysis}</p>
          </Card>
        )}

        {/* Cause Section */}
        <Card className="p-4">
          <h3 className="font-semibold mb-2">Root Cause</h3>
          <p className="text-sm text-muted-foreground">{cause}</p>
        </Card>

        {/* Tools Section */}
        <Card className="p-4">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Wrench className="h-4 w-4" />
            Required Tools
          </h3>
          <div className="flex flex-wrap gap-2">
            {tools.map((tool, idx) => (
              <Badge key={idx} variant="default">{tool}</Badge>
            ))}
          </div>
        </Card>

        <Separator />

        {/* Workflow Steps Visualization */}
        <div>
          <h3 className="text-xl font-bold mb-4 font-display">Workflow Steps</h3>
          <div className="space-y-4">
            {steps.map((step, idx) => {
              const hasConnection = diagram?.some(d => d.from === step.id);
              
              return (
                <React.Fragment key={step.id}>
                  <Card className="p-4 hover:border-primary/50 transition-colors">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">
                        {step.id}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold mb-1">{step.label}</h4>
                        <Badge variant="outline" className="text-xs">
                          {step.type}
                        </Badge>
                      </div>
                    </div>
                  </Card>
                  
                  {hasConnection && idx < steps.length - 1 && (
                    <div className="flex justify-center">
                      <ArrowRight className="h-6 w-6 text-muted-foreground" />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Diagram Connections Summary */}
        {diagram && diagram.length > 0 && (
          <Card className="p-4 bg-muted/30">
            <h3 className="font-semibold mb-3">Flow Connections</h3>
            <div className="space-y-2 text-sm text-muted-foreground">
              {diagram.map((connection, idx) => {
                const fromStep = steps.find(s => s.id === connection.from);
                const toStep = steps.find(s => s.id === connection.to);
                return (
                  <div key={idx} className="flex items-center gap-2">
                    <span className="font-mono text-xs px-2 py-1 bg-background rounded">
                      Step {connection.from}
                    </span>
                    <ArrowRight className="h-4 w-4" />
                    <span className="font-mono text-xs px-2 py-1 bg-background rounded">
                      Step {connection.to}
                    </span>
                    {fromStep && toStep && (
                      <span className="text-xs ml-2">
                        ({fromStep.label} → {toStep.label})
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default SandboxCanvas;
